import { useEffect, useMemo, useState } from 'react';
import {
  FilmIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  XMarkIcon,
  FolderOpenIcon,
} from '@heroicons/react/24/outline';
import { projectMediaApi, ProjectMediaItem } from '@/services/projectMediaApi';

export interface SelectedVideo {
  url: string;
  name: string;
  // Present when the video came from a VideoTranslationJob (i.e. it's a
  // translated/dubbed output, not a plain upload) — lets the clip generator
  // look up its target language and transcript.
  jobId?: string;
}

interface VideoLibraryPickerProps {
  selected: SelectedVideo | null;
  onSelect: (item: SelectedVideo) => void;
  onClear: () => void;
}

export default function VideoLibraryPicker({ selected, onSelect, onClear }: VideoLibraryPickerProps) {
  const [browsing, setBrowsing] = useState(!selected);
  const [items, setItems] = useState<ProjectMediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (selected) setBrowsing(false);
  }, [selected?.url]);

  useEffect(() => {
    if (!browsing || loaded) return;
    setLoading(true);
    setError(null);
    projectMediaApi
      .getMedia('videos')
      .then((res) => {
        setItems(res.data.items.filter((item) => !!item.url));
        setLoaded(true);
      })
      .catch(() => setError('Failed to load your videos'))
      .finally(() => setLoading(false));
  }, [browsing, loaded]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) => item.name.toLowerCase().includes(q) || item.projectName?.toLowerCase().includes(q)
    );
  }, [items, search]);

  const handlePick = (item: ProjectMediaItem) => {
    if (!item.url) return;
    onSelect({ url: item.url, name: item.name, jobId: item.jobId });
    setBrowsing(false);
  };

  if (selected && !browsing) {
    return (
      <div className="border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 min-w-0">
            <FilmIcon className="h-7 w-7 text-blue-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{selected.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Selected from your videos</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setBrowsing(true)}
              className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              Change
            </button>
            <button
              type="button"
              onClick={onClear}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-300 dark:border-gray-600 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search your videos..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        {selected && (
          <button
            type="button"
            onClick={() => setBrowsing(false)}
            className="text-xs font-medium text-gray-600 dark:text-gray-300 hover:underline shrink-0"
          >
            Cancel
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
          <ArrowPathIcon className="h-4 w-4 animate-spin" /> Loading your videos...
        </div>
      )}

      {!loading && error && (
        <p className="text-sm text-red-600 dark:text-red-400 py-4 text-center">{error}</p>
      )}

      {!loading && !error && filteredItems.length === 0 && (
        <div className="text-center py-8">
          <FolderOpenIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {search ? 'No matching videos.' : 'No videos yet — generate or upload one first.'}
          </p>
        </div>
      )}

      {!loading && !error && filteredItems.length > 0 && (
        <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
          {filteredItems.map((item) => (
            <button
              key={item._id}
              type="button"
              onClick={() => handlePick(item)}
              className="w-full flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-2 text-left hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
            >
              <video src={item.url} className="h-12 w-20 rounded object-cover bg-gray-100 dark:bg-gray-900 shrink-0" preload="metadata" muted />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {item.projectName ? `${item.projectName} · ` : ''}
                  {new Date(item.createdAt).toLocaleDateString()}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
